import { Help } from "@mui/icons-material";
import { Box, Button, Collapse, InputAdornment, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPlanState } from "../redux/features/createPlanStateSlice";

export const PlanPriceDetails = ({ setIsFormValid, readOnly = false, onEdit = false }) => {
  const dispatch = useDispatch();
  const newPlanState = useSelector((state) => state.createPlanState);
  const [planTrail, setPlanTrail] = useState(newPlanState.trial);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(createPlanState({ [name]: value }));
  };

  const handlePlanTrial = () => {
    if (planTrail) {
      dispatch(createPlanState({ sequence: 1 }));
      dispatch(createPlanState({ trial: false }));
    } else {
      dispatch(createPlanState({ sequence: 2 }));
      dispatch(createPlanState({ trial: true }));
    }
    setPlanTrail(!planTrail);
  };

  useEffect(() => {
    let isFormValid = newPlanState.price && newPlanState.billingFrequency && newPlanState.currency && newPlanState.intervalCount && newPlanState.totalCycles;
    if (planTrail) {
      isFormValid = isFormValid && newPlanState.priceTrial && newPlanState.frequencyTrial && newPlanState.intervalCountTrial && newPlanState.totalCyclesTrial;
    }
    setIsFormValid(isFormValid);
  }, [
    newPlanState.price,
    newPlanState.billingFrequency,
    newPlanState.currency,
    newPlanState.intervalCount,
    newPlanState.totalCycles,
    newPlanState.priceTrial,
    newPlanState.frequencyTrial,
    newPlanState.intervalCountTrial,
    newPlanState.totalCyclesTrial,
    newPlanState.trial
  ]);

  useEffect(() => {
    setPlanTrail(newPlanState.trial);
  }, [newPlanState.trial])

  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 1,
        backgroundColor: "#white",
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        alignItems: "center",
        transition: "opacity 1s ease-in-out",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        height: "auto",
        marginTop: 4,
        width: { xs: "100%", sm: 500 },
      }}
    >
      <Typography textAlign="center" fontWeight="bold" m={1}>
        Pricing Details
      </Typography>
      <Box
        sx={{
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: 1,
          height: "auto",
          padding: "10px",
        }}
      >
        <TextField
          margin="normal"
          required
          id="outlined-helperText"
          label="Price"
          name="price"
          size="small"
          fullWidth
          type="number"
          variant="outlined"
          sx={{
            fontSize: "12px",
          }}
          onChange={handleInputChange}
          value={newPlanState.price}
          disabled={readOnly}
        />
        <TextField onChange={handleInputChange} select label="Currency" required size="small" sx={{ mt: 1, fontSize: "12px" }} name="currency" value={newPlanState.currency} disabled={readOnly}>
          <MenuItem key={1} value="USD">
            USD
          </MenuItem>
          <MenuItem key={2} value="MXN">
            MXN
          </MenuItem>
        </TextField>
        <Box
          sx={{
            display:  onEdit ? "none" : "flex",
            flexDirection: "row",
            gap: 1,
          }}
        >
          <TextField
            onChange={handleInputChange}
            select
            label="Billing frequency"
            required
            size="small"
            sx={{ mt: 2, fontSize: "12px" }}
            name="billingFrequency"
            value={newPlanState.billingFrequency}
            disabled={readOnly}
            fullWidth
            InputProps={{
              sx: { textAlign: "left" },
            }}
          >
            <MenuItem key={1} value="Week">
              Weekly
            </MenuItem>
            <MenuItem key={2} value="Month">
              Monthly
            </MenuItem>
            <MenuItem key={3} value="Year">
              Yearly
            </MenuItem>
          </TextField>
          <TextField
            fullWidth
            onChange={handleInputChange}
            label="Charge Interval"
            required
            size="small"
            sx={{ mt: 2, fontSize: "12px" }}
            name="intervalCount"
            value={newPlanState.intervalCount}
            type="number"
            disabled={readOnly}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Specify how many times the chosen frequency happens before the next payment. For instance, if the frequency is 'Weekly' and you enter '2', the plan will be charged every 2 weeks.">
                    <Help fontSize="10px" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TextField
          onChange={handleInputChange}
          label="Total Payments"
          required
          size="small"
          sx={{ mt: 2, fontSize: "12px", display: onEdit ? "none" : "" }}
          name="totalCycles"
          value={newPlanState.totalCycles}
          type="number"
          disabled={readOnly}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="How many times will customers be charged for this plan? Once all charges are made, the plan will end. Use '0' to charge customers indefinitely until they cancel.">
                  <Help fontSize="10px" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        ></TextField>
      </Box>

      <Box
        sx={{
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: 1,
          height: "auto",
          padding: "10px",
        }}
      >
        <Collapse in={planTrail}>
          <Typography>Trial Configuration</Typography>

          <TextField
            margin="normal"
            required
            id="outlined-helperText"
            label="Price"
            name="priceTrial"
            size="small"
            fullWidth
            type="number"
            variant="outlined"
            sx={{
              fontSize: "12px",
            }}
            onChange={handleInputChange}
            value={newPlanState.priceTrial}
            disabled={readOnly}
          />

          <Box
            sx={{
              display: onEdit ? "none" : "flex",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <TextField
              onChange={handleInputChange}
              select
              label="Trail frequency"
              required
              size="small"
              sx={{ mt: 1, fontSize: "12px" }}
              name="frequencyTrial"
              value={newPlanState.frequencyTrial}
              disabled={readOnly}
              fullWidth
              InputProps={{
                sx: { textAlign: "left" },
              }}
            >
              <MenuItem key={1} value="Week">
                Weekly
              </MenuItem>
              <MenuItem key={2} value="Month">
                Monthly
              </MenuItem>
              <MenuItem key={3} value="Year">
                Yearly
              </MenuItem>
            </TextField>
            <TextField
              fullWidth
              onChange={handleInputChange}
              label="Charge Interval"
              required
              size="small"
              sx={{ mt: 1, fontSize: "12px" }}
              name="intervalCountTrial"
              value={newPlanState.intervalCountTrial}
              type="number"
              disabled={readOnly}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Specify how many times the chosen frequency happens before the next payment. For instance, if the frequency is 'Weekly' and you enter '2', the plan will be charged every 2 weeks.">
                      <Help fontSize="10px" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TextField
            onChange={handleInputChange}
            label="Total Trail Cycles"
            required
            size="small"
            sx={{ mt: 2, fontSize: "12px", display: onEdit ? "none" : "" }}
            name="totalCyclesTrial"
            value={newPlanState.totalCyclesTrial}
            type="number"
            disabled={readOnly}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="How long will customers have access to the free trial? After the trial period ends, they will be charged according to the plan. For instance, if the frequency is 'Monthly' and you enter '1', the free trial will last for 1 month.">
                    <Help fontSize="10px" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          ></TextField>
        </Collapse>
      </Box>
      {(!readOnly) ? (
        <Button onClick={handlePlanTrial} color={planTrail ? "error" : "primary"} variant="outlined" sx={{ width: 150, display: onEdit ? "none" : "" }}>
          {planTrail ? "Discard Trial" : "Add Trail"}
        </Button>
      ) : (
        <></>
      )}
    </Box>
  );
};
